import { Link } from 'react-router-dom';
import { ENTITIES } from '../data';

interface ChipProps {
  id: string;
}

function Chip({ id }: ChipProps) {
  const e = ENTITIES[id];
  return (
    <Link className="m-chip" to={`/docs/concepts/${id}`}>
      <span className="m-chip-name">{e.name}</span>
    </Link>
  );
}

export function MachineSchema() {
  return (
    <div className="machine-schema">
      <div className="m-header">
        <span className="rv-eyebrow-mini">The revenue engine</span>
        <h3>
          Inputs feed in. <em>Revenue</em> flows out.
        </h3>
        <p>
          Your catalog is the raw material. The Subscription &amp; Pricing engine processes it.
          Invoices, events, and analytics are the exhaust — the record of what happened.
        </p>
      </div>

      <div className="m-diagram">
        <div className="m-inputs">
          <div className="m-col-label">Inputs · catalog</div>
          <div className="m-input-stack">
            <Chip id="featureFlag" />
            <Chip id="meter" />
            <Chip id="variableComponent" />
            <Chip id="module" />
            <Chip id="bundle" />
            <Chip id="region" />
          </div>
          <div className="m-pipe m-pipe-in" />
        </div>

        <div className="m-engine">
          <div className="m-engine-outer">
            <div className="m-engine-ring" />
            <div className="m-engine-ring m-engine-ring-2" />
            <div className="m-engine-core">
              <Link className="m-engine-main" to="/docs/concepts/subscription">
                <div className="m-core-eb">THE HUB</div>
                <div className="m-core-name">Subscription</div>
                <div className="m-core-line">Who, what, how much, how often.</div>
              </Link>
              <Link className="m-engine-bridge" to="/docs/concepts/variableComponentPrice">
                <span>Component Price</span>
                <span className="m-bridge-sub">scoped, versioned, per-currency</span>
              </Link>
              <Link className="m-engine-bridge m-engine-bridge-right" to="/docs/concepts/client">
                <span>Client</span>
                <span className="m-bridge-sub">the customer</span>
              </Link>
              <Link className="m-engine-bridge m-engine-bridge-top" to="/docs/concepts/expansion">
                <span>Expansion</span>
                <span className="m-bridge-sub">mid-cycle changes</span>
              </Link>
            </div>
          </div>
        </div>

        <div className="m-outputs">
          <div className="m-pipe m-pipe-out" />
          <div className="m-col-label">Outputs · what you see</div>
          <div className="m-output-stack">
            <Link className="m-output m-output-invoice" to="/docs/concepts/invoice">
              <div className="m-output-name">Invoices</div>
              <div className="m-output-line">Billing documents, line items.</div>
              <div className="m-output-eg">ACME-2026-04-0007 · $67.33</div>
            </Link>
            <Link className="m-output m-output-event" to="/docs/concepts/lifecycleEvent">
              <div className="m-output-name">Lifecycle Events</div>
              <div className="m-output-line">Append-only log. Source of truth.</div>
              <div className="m-output-eg">EXPANDED · +$25 MRR</div>
            </Link>
            <div className="m-output m-output-analytics">
              <div className="m-output-name">Analytics</div>
              <div className="m-output-line">MRR, ARR, NRR, churn — all derived.</div>
              <div className="m-metric-grid">
                <div>
                  <span>MRR</span>
                  <strong>$12,540</strong>
                </div>
                <div>
                  <span>ARR</span>
                  <strong>$150,486</strong>
                </div>
                <div>
                  <span>Net churn</span>
                  <strong>2.4%</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Link className="m-tenant-strip" to="/docs/concepts/tenant">
        <span>
          All of the above is scoped to your <strong>Tenant</strong> — your slice of Revvly. No one
          else sees it.
        </span>
        <span className="m-tenant-arrow">›</span>
      </Link>
    </div>
  );
}
